import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Box,
  Typography,
  Container,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { isValidEmail } from '../utils/validators';

const ERS_URL = (process.env.REACT_APP_ERS_URL || '').replace(/\/$/, '');

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitErrorCode, setSubmitErrorCode] = useState('');
  const [ersUrl, setErsUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth();

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'rememberMe' ? checked : value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setSubmitError('');
    setSubmitErrorCode('');
    setErsUrl('');
    
    try {
      const result = await login({
        email: formData.email,
        password: formData.password
      });
      
      console.log("Login result:", result);
      
      if (!result.success) {
        const isInvalidCredentials =
          (result.error || '').toLowerCase().includes('invalid credentials');
        setSubmitError(
          isInvalidCredentials
            ? 'Incorrect email or password. Please try again.'
            : (result.error || 'Login failed')
        );
        setSubmitErrorCode(result.code || '');
        setErsUrl(result.ersUrl || ERS_URL);
      }
      
      if (formData.rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
    } catch (error) {
      setSubmitError(error.message || 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({
        ...prev,
        email: rememberedEmail,
        rememberMe: true
      }));
    }
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#FEFEFE',
      }}
    >
      {/* Left Side - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FEFEFE',
          padding: 4,
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ width: '100%', maxWidth: 440, margin: '0 auto' }}>
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontFamily: '"Playfair Display", "Lora", serif',
                  fontWeight: 700,
                  mb: 1.5,
                  color: '#000000',
                  fontSize: { xs: '2.75rem', sm: '3.5rem' },
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                Welcome Back
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"Inter", "Lora", sans-serif',
                  color: 'rgba(35,31,32,0.6)',
                  fontSize: '1rem',
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                }}
              >
                Enter your credentials to access your account
              </Typography>
            </Box>

            {submitError && submitErrorCode === 'DASHBOARD_ACCESS_DENIED' && (
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  borderRadius: '12px',
                  backgroundColor: '#F4F7FB',
                  color: '#231F20',
                  border: '1px solid #D7E3F4',
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  You don&apos;t have access to Envoy Dashboard
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                  {submitError}
                </Typography>
                {ersUrl && (
                  <Button
                    href={ersUrl}
                    variant="contained"
                    size="small"
                    sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700 }}
                  >
                    Open the Employee Roster System
                  </Button>
                )}
              </Alert>
            )}

            {submitError && submitErrorCode !== 'DASHBOARD_ACCESS_DENIED' && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: '12px',
                  backgroundColor: '#FFF5F5',
                  color: '#C41E3A',
                  border: '1px solid #FFEBEE',
                  '& .MuiAlert-icon': {
                    color: '#C41E3A'
                  }
                }}
              >
                {submitError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#231F20',
                    mb: 1,
                    display: 'block',
                  }}
                >
                  Email Address
                </Typography>
                <TextField
                  required
                  fullWidth
                  id="email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: '"Inter", sans-serif',
                      borderRadius: '12px',
                      backgroundColor: '#F9FAFB',
                      border: '2px solid transparent',
                      transition: 'all 0.2s ease-in-out',
                      '& fieldset': {
                        border: '2px solid #E5E7EB',
                        transition: 'all 0.2s ease-in-out',
                      },
                      '&:hover': {
                        backgroundColor: '#F3F4F6',
                        '& fieldset': {
                          borderColor: '#D1D5DB',
                        },
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: '#000000',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-error fieldset': {
                        borderColor: '#C41E3A',
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '14px 16px',
                      fontSize: '0.95rem',
                      color: '#231F20',
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#231F20',
                    mb: 1,
                    display: 'block',
                  }}
                >
                  Password
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            color: 'rgba(35,31,32,0.5)',
                            '&:hover': {
                              color: '#231F20',
                            }
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: '"Inter", sans-serif',
                      borderRadius: '12px',
                      backgroundColor: '#F9FAFB',
                      transition: 'all 0.2s ease-in-out',
                      '& fieldset': {
                        border: '2px solid #E5E7EB',
                        transition: 'all 0.2s ease-in-out',
                      },
                      '&:hover': {
                        backgroundColor: '#F3F4F6',
                        '& fieldset': {
                          borderColor: '#D1D5DB',
                        },
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: '#000000',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-error fieldset': {
                        borderColor: '#C41E3A',
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '14px 16px',
                      fontSize: '0.95rem',
                      color: '#231F20',
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, mt: 2.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      disabled={loading}
                      sx={{
                        color: 'rgba(35,31,32,0.3)',
                        '&.Mui-checked': {
                          color: '#000000',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: 20,
                        }
                      }}
                    />
                  }
                  label={
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: '"Inter", sans-serif',
                        color: 'rgba(35,31,32,0.7)',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      Remember me
                    </Typography>
                  }
                />
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#231F20',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      width: '0%',
                      height: '1px',
                      bottom: '-2px',
                      left: 0,
                      backgroundColor: '#000000',
                      transition: 'width 0.3s ease',
                    },
                    '&:hover::after': {
                      width: '100%',
                    }
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  py: 1.75,
                  mb: 3.5,
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  letterSpacing: '0.03em',
                  backgroundColor: '#000000',
                  color: '#FEFEFE',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#1a1a1a',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&:disabled': {
                    backgroundColor: '#E5E7EB',
                    color: 'rgba(35,31,32,0.4)',
                    boxShadow: 'none',
                  }
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: '"Inter", sans-serif',
                    color: 'rgba(35,31,32,0.6)',
                    fontSize: '0.9rem',
                  }}
                >
                  Don't have an account?{' '}
                  <Link
                    component={RouterLink}
                    to="/register"
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      color: '#000000',
                      textDecoration: 'none',
                      fontWeight: 700,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: '100%',
                        height: '1px',
                        bottom: '-2px',
                        left: 0,
                        backgroundColor: '#000000',
                        transition: 'all 0.3s ease',
                      },
                      '&:hover::after': {
                        height: '2px',
                      }
                    }}
                  >
                    Sign Up
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Right Side - Branding */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#000000',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            color: '#FEFEFE',
            zIndex: 1,
            px: 6,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontFamily: '"Playfair Display", "Lora", serif',
              fontWeight: 700,
              mb: 2,
              letterSpacing: '0.05em',
              fontSize: { md: '3.5rem', lg: '4.5rem' },
              lineHeight: 1.1,
            }}
          >
            ENVOY
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: '"Italiana", "Lora", serif',
              fontWeight: 400,
              fontStyle: 'italic',
              fontSize: { md: '2.5rem', lg: '3.5rem' },
              color: '#FFFFFF',
              letterSpacing: '0.03em',
              opacity: 0.95,
            }}
          >
            Dashboard
          </Typography>
          
          <Box
            sx={{
              width: '80px',
              height: '2px',
              backgroundColor: '#FFFFFF',
              margin: '2.5rem auto',
              opacity: 0.6,
            }}
          />
          
          <Typography
            variant="body1"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.95rem',
              fontWeight: 400,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            "Shape Your Tech."
          </Typography>
        </Box>

        {/* Subtle decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            top: '-250px',
            right: '-250px',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '350px',
            height: '350px',
            borderRadius: '50%',
            border: '1px solid rgba(255, 255, 255, 0.03)',
            bottom: '-175px',
            left: '-175px',
          }}
        />
        
        {/* Minimalist corner accent */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            right: 40,
            width: '60px',
            height: '60px',
            borderRight: '2px solid rgba(255, 255, 255, 0.2)',
            borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
          }}
        />
      </Box>
    </Box>
  );
};

export default Login;